
import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, getDay, addWeeks, subWeeks } from 'date-fns';
import { getDateAvailability } from './utils/dateAvailability';
import { TeacherAvailabilityException, WeeklySchedule, DAY_NAMES } from '@/types/teacherAvailability';
import { ExceptionFormDialog } from './ExceptionFormDialog';
import { useTeacherAvailabilityExceptions } from '@/hooks/useTeacherAvailabilityExceptions';

interface WeeklyViewProps {
  weeklySchedule: WeeklySchedule;
  exceptions: TeacherAvailabilityException[];
  teacherCode?: string;
  onExceptionUpdate?: () => void;
}

export function WeeklyView({ weeklySchedule, exceptions, teacherCode, onExceptionUpdate }: WeeklyViewProps) {
  const [weekViewDate, setWeekViewDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { deleteException } = useTeacherAvailabilityExceptions(teacherCode);

  // Weekly view data
  const weeklyData = useMemo(() => {
    const start = startOfWeek(weekViewDate, { weekStartsOn: 1 }); // Monday
    const end = endOfWeek(weekViewDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    
    return days.map(date => ({
      date,
      dayName: DAY_NAMES[getDay(date)],
      availability: getDateAvailability(date, weeklySchedule, exceptions)
    }));
  }, [weekViewDate, weeklySchedule, exceptions]);

  const handlePreviousWeek = () => {
    setWeekViewDate(prev => subWeeks(prev, 1));
  };

  const handleNextWeek = () => {
    setWeekViewDate(prev => addWeeks(prev, 1));
  };

  const handleGoToCurrentWeek = () => {
    setWeekViewDate(new Date());
  };

  const handleAddException = (dateStr: string) => {
    setSelectedDate(dateStr);
    setIsDialogOpen(true);
  };

  const handleDeleteException = async (dateStr: string) => {
    const existingException = exceptions.find(ex => ex.exception_date === dateStr);
    if (existingException && confirm('คุณต้องการลบวันหยุดการสอนนี้หรือไม่?')) {
      const success = await deleteException(existingException.id);
      if (success && onExceptionUpdate) {
        onExceptionUpdate();
      }
    }
  };

  const handleExceptionAdded = () => {
    setIsDialogOpen(false);
    setSelectedDate(null);
    if (onExceptionUpdate) {
      onExceptionUpdate();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-4">
        <div className="flex flex-col xs:flex-row items-start xs:items-center gap-2 xs:gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span>ช่วงเวลาสอน</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded"></div>
            <span>ช่วงเวลาหยุดการสอน</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handlePreviousWeek}
            className="flex items-center gap-1 text-xs px-2 py-1"
          >
            <ChevronLeft size={14} />
            <span className="hidden xs:inline">สัปดาห์ก่อน</span>
            <span className="xs:hidden">ก่อน</span>
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleGoToCurrentWeek}
            className="px-2 text-xs"
          >
            วันนี้
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleNextWeek}
            className="flex items-center gap-1 text-xs px-2 py-1"
          >
            <span className="hidden xs:inline">สัปดาห์หน้า</span>
            <span className="xs:hidden">หน้า</span>
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>

      <div className="text-center text-sm text-muted-foreground mb-2">
        {format(startOfWeek(weekViewDate, { weekStartsOn: 1 }), 'dd/MM/yyyy')} - {format(endOfWeek(weekViewDate, { weekStartsOn: 1 }), 'dd/MM/yyyy')}
      </div>

      {/* Mobile: Vertical layout, Desktop: Grid layout */}
      <div className="flex flex-col gap-3 md:grid md:grid-cols-7 md:gap-2">
        {weeklyData.map(({ date, dayName, availability }) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const existingException = exceptions.find(ex => ex.exception_date === dateStr);
          
          return (
            <Card 
              key={date.toISOString()} 
              className={`p-3 md:min-h-[200px] ${
                availability.status === 'available' ? 'bg-green-50 border-green-200' :
                availability.status === 'exception' ? 'bg-orange-50 border-orange-200' :
                'bg-gray-50'
              }`}
            >
              <div className="flex md:flex-col items-start md:items-center md:text-center gap-3 md:gap-0">
                {/* Date Header */}
                <div className="flex-shrink-0 md:w-full">
                  <div className="font-medium text-sm md:text-base">{dayName}</div>
                  <div className="text-xs text-muted-foreground md:mb-2">
                    {format(date, 'dd/MM')}
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 md:w-full">
                  {/* Action buttons */}
                  {teacherCode && (
                    <div className="flex justify-start md:justify-center gap-1 mb-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddException(dateStr)}
                        className="p-1 h-6 w-6"
                        title="เพิ่มวันหยุดการสอน"
                      >
                        <Plus size={12} />
                      </Button>
                      {existingException && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteException(dateStr)}
                          className="p-1 h-6 w-6"
                          title="ลบวันหยุดการสอน"
                        >
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Time slots - horizontal on mobile, vertical on desktop */}
                  <div className="flex flex-wrap gap-1 md:flex-col md:space-y-1">
                    {availability.status === 'exception' && availability.type === 'NO_WORK' && (
                      <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">
                        ไม่รับสอนทั้งวัน
                      </Badge>
                    )}
                    
                    {availability.status === 'exception' && availability.type === 'PARTIAL_UNAVAILABLE' && (
                      <>
                        <div className="w-full text-xs text-red-600 font-medium mb-1 md:text-center">ไม่รับสอน:</div>
                        {availability.timeSlots.map((slot, index) => (
                          <Badge 
                            key={index}
                            variant="secondary"
                            className="text-xs bg-red-100 text-red-800 md:block md:mb-1"
                          >
                            {slot}
                          </Badge>
                        ))}
                        
                        {availability.basicTimeSlots && availability.basicTimeSlots.length > 0 && (
                          <>
                            <div className="w-full text-xs text-green-600 font-medium mt-2 mb-1 md:text-center">รับสอน:</div>
                            {availability.basicTimeSlots.map((slot, index) => (
                              <Badge 
                                key={index}
                                variant="secondary"
                                className="text-xs bg-green-100 text-green-800 md:block md:mb-1"
                              >
                                {slot.start_time} - {slot.end_time}
                              </Badge>
                            ))}
                          </>
                        )}
                      </>
                    )}
                    
                    {availability.status === 'available' && availability.timeSlots.map((slot, index) => (
                      <Badge 
                        key={index}
                        variant="secondary"
                        className="text-xs bg-green-100 text-green-800 md:block md:mb-1"
                      >
                        {slot}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Exception Form Dialog */}
      <ExceptionFormDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedDate(null);
        }}
        teacherCode={teacherCode || ''}
        selectedDate={selectedDate || ''}
        onSuccess={handleExceptionAdded}
      />
    </div>
  );
}
